import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Users, DollarSign, Activity, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PageHeader from '../components/PageHeader';
import { analyticsData, testimonials } from '../mock';

const tooltipStyle = {
  backgroundColor: 'hsl(var(--raise))',
  border: '1px solid hsl(var(--line))',
  borderRadius: '10px',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '12px',
  color: 'hsl(var(--ink))',
};

export default function AnalyticsPage() {
  const [data, setData] = useState(
    analyticsData.map((d) => ({ ...d, revenue: 0, users: 0, engagement: 0 }))
  );

  useEffect(() => {
    const t = setTimeout(() => setData(analyticsData), 350);
    return () => clearTimeout(t);
  }, []);

  const metrics = [
    { icon: DollarSign, label: 'Revenue Growth', value: '+45%', color: 'from-purple-500 to-purple-600' },
    { icon: Users, label: 'Active Users', value: '24.8K', color: 'from-pink-500 to-pink-600' },
    { icon: TrendingUp, label: 'Conversion Rate', value: '+32%', color: 'from-purple-500 to-pink-600' },
    { icon: Activity, label: 'Engagement', value: '87%', color: 'from-pink-500 to-purple-600' },
  ];

  return (
    <>
      <PageHeader
        index="// 03"
        eyebrow="Analytics Demo"
        title="Real-time insight,"
        accent="rendered live."
        intro="A working snapshot of the dashboards we build — metrics, trends, and visualization powered by your own data."
      />

      <section className="relative py-20 bg-surface overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bloom rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {metrics.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.45 }}
                >
                  <Card className="bg-raise border-line hover:border-purple-500/40 transition-all duration-300">
                    <CardContent className="p-5 flex items-center justify-between">
                      <div>
                        <p className="font-mono-ui text-[11px] text-faint tracking-wider mb-1">{m.label}</p>
                        <p className="font-display text-3xl font-bold text-strong">{m.value}</p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${m.color} flex items-center justify-center shadow-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-raise border-line">
              <CardHeader>
                <CardTitle className="font-display text-strong">Revenue Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a855f7" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--line))" />
                    <XAxis dataKey="month" stroke="hsl(var(--ink-faint))" fontSize={12} />
                    <YAxis stroke="hsl(var(--ink-faint))" fontSize={12} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'hsl(var(--ink))' }} />
                    <Area type="monotone" dataKey="revenue" stroke="#a855f7" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={2.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-raise border-line">
              <CardHeader>
                <CardTitle className="font-display text-strong">User Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--line))" />
                    <XAxis dataKey="month" stroke="hsl(var(--ink-faint))" fontSize={12} />
                    <YAxis stroke="hsl(var(--ink-faint))" fontSize={12} />
                    <Tooltip contentStyle={{ ...tooltipStyle, border: '1px solid #ec4899' }} labelStyle={{ color: 'hsl(var(--ink))' }} />
                    <Bar dataKey="users" fill="#ec4899" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="bg-raise border-line lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-display text-strong">Performance Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--line))" />
                    <XAxis dataKey="month" stroke="hsl(var(--ink-faint))" fontSize={12} />
                    <YAxis stroke="hsl(var(--ink-faint))" fontSize={12} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: 'hsl(var(--ink))' }} />
                    <Legend wrapperStyle={{ color: 'hsl(var(--ink))', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }} />
                    <Line type="monotone" dataKey="revenue" stroke="#a855f7" strokeWidth={3} dot={{ fill: '#a855f7', r: 4 }} />
                    <Line type="monotone" dataKey="users" stroke="#ec4899" strokeWidth={3} dot={{ fill: '#ec4899', r: 4 }} />
                    <Line type="monotone" dataKey="engagement" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative py-20 border-t border-line bg-surface overflow-hidden">
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <span className="eyebrow text-purple-500">// proof</span>
            <span className="eyebrow">What clients say</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="rounded-2xl border border-line bg-raise p-7 hover:border-purple-500/40 transition-all duration-300"
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-purple-400 text-purple-500" />
                  ))}
                </div>
                <p className="text-soft leading-relaxed mb-6">"{t.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center font-display font-semibold text-white text-sm">
                    {t.name.split(' ').map((n) => n[0]).join('')}
                  </div>
                  <div>
                    <div className="text-strong font-medium text-sm">{t.name}</div>
                    <div className="font-mono-ui text-[11px] text-faint">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
